/**
 * SpawnAppraisalVariant · iter303ak-fix25 — Declined · T30 · Requote branch · A10
 * ─────────────────────────────────────────────────────────────────────────────
 * Deep-link launcher — opens the parent appraisal's Roof Appraisal tab in
 * embed mode (existing `?embed=roof_appraisal` popout) so the operator
 * can amend the declined quote or add a fresh variant. On save & close,
 * Continue unlocks and the next action (checkbox_tick) flips the
 * appraisal status back to Quote Sent.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ExternalLink, CheckCircle2, RefreshCw, ArrowUpRight } from 'lucide-react';
import { QUOTE_TYPE_TO_SUBTAB } from './QuoteBuilderLauncher';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function SpawnAppraisalVariant({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const parentAppraisalNo = capturedData?.appraisal?.appraisal_no
    || capturedData?.appraisal_record?.appraisal_no;

  const declinedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'declined')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [popupWin, setPopupWin] = useState(null);
  const [visitedAt, setVisitedAt] = useState(value?.visited_at || null);
  const [closedAt, setClosedAt] = useState(value?.closed_at || null);
  const [childCountBefore, setChildCountBefore] = useState(value?.child_count_before || null);
  const [childCountAfter, setChildCountAfter] = useState(value?.child_count_after || null);
  const [children, setChildren] = useState([]);

  // Snapshot number of children at first mount so we can detect if a
  // new variant was added while the operator was in the popup.
  useEffect(() => {
    if (!appraisalId || childCountBefore !== null) return;
    (async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/children`);
        setChildren((data?.children || []).filter(c => !c.is_draft));
        setChildCountBefore((data?.children || []).length);
      } catch { /* silent */ }
    })();
  }, [appraisalId, childCountBefore]);

  // Poll for popup close → snapshot child count again + stamp closed_at.
  useEffect(() => {
    if (!popupWin) return undefined;
    const t = setInterval(async () => {
      if (popupWin.closed) {
        clearInterval(t);
        setPopupWin(null);
        setClosedAt(new Date().toISOString());
        if (appraisalId) {
          try {
            const { data } = await api.get(`/appraisals/${appraisalId}/children`);
            setChildCountAfter((data?.children || []).length);
          } catch { /* silent */ }
        }
      }
    }, 500);
    return () => clearInterval(t);
  }, [popupWin, appraisalId]);

  useEffect(() => {
    if (!onChange) return;
    onChange({
      visited_at: visitedAt,
      closed_at: closedAt,
      child_count_before: childCountBefore,
      child_count_after: childCountAfter,
      variant_added: (childCountBefore !== null && childCountAfter !== null) ? (childCountAfter > childCountBefore) : false,
    });
  }, [visitedAt, closedAt, childCountBefore, childCountAfter, onChange]);

  // Same per-quote builder embed the Quote Builder step uses (amend, or add a variant from inside it).
  const openQuoteBuilder = (child) => {
    if (!appraisalId) return;
    const targetId = child?.id || appraisalId;
    const subtab = QUOTE_TYPE_TO_SUBTAB(child?.quote_type_ref || '');
    const url = `/appraisals/${targetId}?embed=quote_builder&builder=${encodeURIComponent(subtab)}`;
    const win = window.open(url, `quote_builder_${targetId}`, 'width=1600,height=1000');
    if (!win) { alert('Popup blocked — allow popups for this site and try again.'); return; }
    setPopupWin(win);
    setVisitedAt(new Date().toISOString());
  };

  const rescanChildren = async () => {
    if (!appraisalId) return;
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/children`);
      setChildCountAfter((data?.children || []).length);
    } catch { /* silent */ }
  };

  const variantAdded = (childCountBefore !== null && childCountAfter !== null) && childCountAfter > childCountBefore;
  const canAdvance = !!visitedAt; // Operator must at least open the popup once.
  const declinedIds = new Set(declinedChildren.map(c => c.child_id));
  const targets = children.filter(c => declinedIds.size === 0 || declinedIds.has(c.id));

  return (
    <div className="space-y-4" data-testid="wf-spawn-appraisal-variant">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <ArrowUpRight className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Open Quote Builder — Amend or Add a Variant</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'The client asked for changes. Open the declined quote in its builder, then either amend it or add a fresh variant there. Save & close the popup when done.'}
            </div>
          </div>
        </div>
      </div>

      {parentAppraisalNo && (
        <div className="text-[11px] text-slate-600">
          Working on parent: <b>A{parentAppraisalNo}</b>
          {declinedChildren.length > 0 && (
            <span className="ml-2 text-slate-500">
              · Declined child(ren) to consider: {declinedChildren.map(c => `A${c.appraisal_no}`).join(', ')}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg border border-slate-200 bg-white">
        {targets.length === 0 ? (
          <button type="button" onClick={() => openQuoteBuilder(null)} disabled={!appraisalId}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-spawn-open-builder">
            <ExternalLink size={14} /> Open Quote Builder
          </button>
        ) : targets.map(c => (
          <button key={c.id} type="button" onClick={() => openQuoteBuilder(c)}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 inline-flex items-center gap-2"
            data-testid={`wf-spawn-open-builder-${c.id}`}>
            <ExternalLink size={14} /> Open A{c.appraisal_no} · {c.quote_type_ref || 'Quote'}
          </button>
        ))}
        {visitedAt && (
          <button
            type="button"
            onClick={rescanChildren}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-indigo-700 bg-white border border-indigo-300 hover:bg-indigo-50 inline-flex items-center gap-1.5"
            data-testid="wf-spawn-rescan"
          >
            <RefreshCw size={12} /> Re-scan for new variant
          </button>
        )}
      </div>

      {visitedAt && (
        <div className="p-3 border border-slate-200 rounded-lg bg-slate-50 text-xs text-slate-700 space-y-1">
          <div>Builder popup opened at <b>{new Date(visitedAt).toLocaleString()}</b></div>
          {closedAt && <div className="text-emerald-700">✓ Popup closed at {new Date(closedAt).toLocaleString()}</div>}
          {childCountBefore !== null && (
            <div>
              Children count: <b>{childCountBefore}</b> before
              {childCountAfter !== null && <> → <b>{childCountAfter}</b> after</>}
              {variantAdded && <span className="ml-2 text-emerald-700 font-semibold">✓ New variant detected</span>}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-spawn-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-spawn-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!canAdvance}
            className="px-4 py-1.5 rounded-md text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-spawn-continue"
            title={canAdvance ? 'Continue to the checkbox' : 'Open the Quote Builder first'}
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
